import { useState } from 'react';
import type {
  IUserSettingsPredefinedField,
  IUserSettingsSubmittalUdf,
  SubmittalDragType,
} from 'app/services/userSettingsApi';
import {
  deleteButtonWantsTop,
  type SubmittalCmsDocument,
  type SubmittalDragPayload,
  type SubmittalLibraryType,
} from './submittalModel';

/**
 * Column 2 of the Submittal tab — `submittal.tmpl:38-145`. The **library**: the
 * three sources you can drop into the format rail, their chips, the search box
 * and the "Total Fields" counters.
 *
 * ★ THREE SOURCES, ONE AT A TIME.
 *  · **User** (283 rows) — author-owned fields, each either a rich-text block or
 *    an uploaded PDF cut sheet. Only this list gets a search box, a `+` button
 *    and a per-row trash button.
 *  · **CMS** (6 rows) — company boilerplate derived from the `CMSAHUTerms`
 *    payload (see `deriveSubmittalCmsDocuments`). Read-only here.
 *  · **Application** (20 rows) — engine-generated report sections, each with a
 *    static preview image in column 3.
 * All three `<ul>`s are always in the DOM; `.show-row` (and Bootstrap-free
 * `display` rules in `styles.css`) picks the visible one. Legacy toggles the
 * class imperatively in `changeActiveDefinedsField` and re-derives it from
 * `activeDefinedsField` on the next full render — the same single source of
 * truth this component renders from.
 *
 * ★ `id="myTab"` APPEARS THREE TIMES HERE (once per `<ul>`), plus more on the
 * Proposal tab — part of Q3's duplicate-id family. Kept.
 *
 * ★ Q6 — the counters read the SNAPSHOT length and are never touched by the
 * search. Typing `damper` leaves 39 of 283 `<li>`s visible and the label still
 * reads 283. Do not "fix" it by counting visible rows.
 *
 * ★ THE SEARCH FILTERS ON THE SNAPSHOT'S NAME, not the live one. Legacy filters
 * on the `<li>`'s `data-name` ATTRIBUTE, which a rename never rewrites
 * (`saveUserDefinedFieldData` only calls jQuery's `.data()` on the FORMAT rows,
 * and rewrites the library anchor's `innerText` — never the `<li>` attribute).
 * So a renamed field keeps matching its OLD name until the next full render.
 * `data-name` below is fed from the render snapshot for exactly that reason,
 * while the anchor's text is fed from the rename overlay.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * ★ U-d — THE ANCHORS ARE THE DRAG SOURCES, AND THE MARKUP WAS ALREADY RIGHT.
 * They already carried `draggable="true"`, `.submittal-draggable-item`,
 * `.submittal-item-for-drag` and `data-type` / `data-id` / `data-name` /
 * `data-text`, which is the entire payload `createNewField`
 * (`submittal.js:786-831`) reads off the dropped element — so U-d added
 * handlers, not markup, via `anchorDragProps` below.
 *
 * ★ …but the handler is a `mousedown`, not a `dragstart` (2026-09-09). Legacy
 * runs `draggable({helper: "clone", revert: true})` over these anchors, which is
 * a MOUSE drag — the `draggable="true"` attribute in the template never gets to
 * do anything, because jQuery-UI cancels the press that would start the native
 * drag. `useLibraryDrag` is that widget; `anchorDragProps` arms it and cancels
 * `dragstart` for the same reason.
 *
 * ★ `.submittal-item-for-drag` ON A LIBRARY ANCHOR IS NOT DECORATION. It is the
 * same class the rail's sortable declares as its `items`, which is precisely
 * what lets jQuery-UI's `connectToSortable` hand the anchor over and have the
 * sortable accept it as one of its own rows. The native port does not need the
 * coincidence, but the class stays — `styles.css` may key off it, and removing
 * it would be exactly the legacy-CSS-selector-miss failure class.
 * ═════════════════════════════════════════════════════════════════════════════
 */

interface FieldLibraryProps {
  /** The render snapshot's user rows — NOT the live collection (see banner). */
  userFields: IUserSettingsSubmittalUdf[];
  cmsDocuments: SubmittalCmsDocument[];
  appFields: IUserSettingsPredefinedField[];
  /** `self.activeDefinedsField`. ★ Can legitimately be `undefined`: clicking the
   * `+` button while the User chip is `.not-active` bubbles to the chip handler
   * with a target that has no `data-type`, and legacy assigns that `undefined`
   * straight onto `self.activeDefinedsField` — blanking every chip, every
   * `.show-row` and every counter until another chip is clicked. Reproduced. */
  activeType: SubmittalLibraryType | undefined;
  counts: { user: number; cms: number; application: number };
  /** `search()` (`:935-945`) — lower-cased substring over `data-name`. */
  searchValue: string;
  /** `changeActiveDefinedsField` `.hide`s BOTH the wrapper div and the input
   * (they share the `submittal-search-field` class). ★ A full `render()` does
   * NOT restore it — the template emits the box unhidden — so creating a UDF
   * while the CMS chip is active brings the search box back. Reproduced by
   * resetting this flag in `doRender`. */
  searchHidden: boolean;
  onSearchChange: (value: string) => void;
  /** `click .submittal-block-fields-wrap.not-active` → `changeActiveDefinedsField`.
   * Fires ONLY for a chip that is currently inactive, and reports the clicked
   * element's own `data-type` (which the `+` button does not have). */
  onChipClick: (type: SubmittalLibraryType | undefined) => void;
  /** `click #open_user_defined_field_pop_up` → the type-select popup (U-c W1). */
  onAddClick: () => void;
  /** `click .each-element-row` → `clickToItem`, PLUS Bootstrap's
   * `data-toggle="tab"` activation of the anchor. One handler here: the two are
   * indistinguishable from the outside because the `<a>` fills the row. */
  onItemClick: (type: SubmittalLibraryType, item: LibraryItemKey) => void;
  /** `mouseover .submittal-row .user-fields-block.nav-tabs a` → `loadPdf`
   * (`:599-618`) — ★ Q1's other half: this, and only this, fills a user detail
   * pane. */
  onUserAnchorMouseOver: (field: IUserSettingsSubmittalUdf) => void;
  /** `click button.remove` → `render_popup` (`:620-636`). Note the bound
   * selector is `button.remove`, so — unlike the format rail's `.delete` (Q4) —
   * the nested `<i class="icon-trash remove">` does NOT match a second time and
   * exactly ONE popup opens. */
  onRemoveClick: (fieldId: number) => void;
  /** Which `<li>`s carry `.active`. Normally exactly one — Bootstrap's tab
   * plugin, plus the three `*_tab` handlers that strip it from the other two
   * blocks — but `active_last_change` (`:478-484`) uses `addClass` without a
   * matching remove, so legacy can legitimately paint TWO grey user rows at
   * once. A SET, not a scalar, for that reason. */
  activeItemKeys: ReadonlySet<string>;
  /** Rename overlay applied to the ANCHOR TEXT only — `saveUserDefinedFieldData`
   * (`:466-469`) sets `a.innerText` and returns. */
  labelOverrides: Record<number, string>;
  /** `showTooltip` (`:884-886`) walks FIVE `parentNode`s up from an overflowing
   * `a.user-defiened-fields`, landing on the `<li>`. */
  tooltipItemKeys: ReadonlySet<string>;
  /** Registered so `clickToItem`'s `scrollIntoView` and the create flow's
   * focus-the-new-title step can find their row. */
  itemRefs: React.MutableRefObject<Map<string, HTMLLIElement>>;
  anchorRefs: React.MutableRefObject<Map<string, HTMLAnchorElement>>;
  /** U-d — the press that arms `draggable({connectToSortable})` (`:862-878`).
   * Hands `useLibraryDrag` the four attributes the drop will read, i.e. legacy's
   * `self.dropping = true` payload. The clone helper, the `distance: 1`
   * threshold, `revert: true` and the `stop` handler all live in that hook,
   * because that is one widget and it should be in one place. */
  onItemMouseDown: (e: React.MouseEvent<HTMLAnchorElement>, payload: SubmittalDragPayload) => void;
}

/** Identifies one library row across the three heterogeneous sources. */
export interface LibraryItemKey {
  /** `user:<id>` / `cms:<value>` / `app:<value>` — see `libraryItemKey`. */
  key: string;
  /** The `<li>`'s `data-id`: user → `<id>`, cms → `<value>`, app → `app-<value>`. */
  liId: string;
  /** The `<a>`'s `data-id` — user/app only; CMS anchors have none. */
  anchorId?: number;
  /** The `<a>`'s `data-cms` — CMS only. */
  cms?: string;
  /** The `tab-pane` this row activates. */
  paneId: string;
}

/** `user:<id>` / `cms:<value>` / `app:<value>` — the single "which row is
 * `.active`" key, because the three `*_tab` handlers make the three `<ul>`s
 * behave as one exclusive group. */
export const libraryItemKey = (type: SubmittalLibraryType, id: string | number) =>
  `${type}:${id}`;

const FieldLibrary = ({
  userFields,
  cmsDocuments,
  appFields,
  activeType,
  counts,
  searchValue,
  searchHidden,
  onSearchChange,
  onChipClick,
  onAddClick,
  onItemClick,
  onUserAnchorMouseOver,
  onRemoveClick,
  activeItemKeys,
  labelOverrides,
  tooltipItemKeys,
  itemRefs,
  anchorRefs,
  onItemMouseDown,
}: FieldLibraryProps) => {
  /**
   * ★ U-d — the drag handlers, spread identically onto all three anchor kinds.
   *
   * The payload is read back OFF THE ELEMENT'S ATTRIBUTES rather than closed
   * over from the row data, because that is literally what `createNewField`
   * does (`submittal.js:788-791`, four `e.attr(...)` calls on the dropped
   * helper) and it is the reason the three drops send three different bodies:
   * a missing attribute is `undefined`, and `JSON.stringify` drops undefined
   * keys, which is how a CMS drop ends up with **no `object_id`** and a user
   * drop with no `cms_field_name`. Deriving the payload from props would
   * quietly turn those absences into `null`s and change the wire.
   *
   * `data-type || 'user_defined'` keeps the `||` (not `??`): the user anchors
   * have no `data-type` at all, and legacy's fallback also catches an empty one.
   */
  const anchorDragProps = {
    onMouseDown: (e: React.MouseEvent<HTMLAnchorElement>) => {
      const a = e.currentTarget;
      onItemMouseDown(e, {
        type: (a.getAttribute('data-type') as SubmittalDragType | null) || 'user_defined',
        name: a.getAttribute('data-name') ?? undefined,
        id: a.getAttribute('data-id') ?? undefined,
        text: a.getAttribute('data-text') ?? '',
      });
      // The anchor sits inside a clickable `<li>`; keep the press from bubbling
      // into anything that might also arm a drag.
      e.stopPropagation();
    },
    /**
     * ★ THE `draggable="true"` ATTRIBUTE STAYS, THE NATIVE DRAG DOES NOT.
     * The attribute is in the legacy template, so it is rendered — but legacy
     * never gets a native drag out of it either: jQuery-UI's `_mouseDown`
     * `preventDefault`s the press, which is what stops the browser starting
     * one. Cancelling `dragstart` is the same thing a beat later, and it leaves
     * the anchor's focus behaviour alone. Without it the browser would run a
     * second, competing drag whose drop targets are every text input on the
     * page.
     */
    onDragStart: (e: React.DragEvent<HTMLAnchorElement>) => e.preventDefault(),
  };

  /** Q20's `.top` flip on the per-row trash buttons — see `FormatRail`. */
  const [topButtons, setTopButtons] = useState<ReadonlySet<number>>(() => new Set());
  const flipTop = (id: number, target: HTMLElement) => {
    const wants = deleteButtonWantsTop(target);
    setTopButtons((prev) => {
      if (wants === prev.has(id)) return prev;
      const next = new Set(prev);
      if (wants) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const chipClass = (type: SubmittalLibraryType) =>
    `block-fields-wrap submittal-block-fields-wrap ${type !== activeType ? 'not-active' : ''}`;

  const searchClass = (base: string) => `${base}${searchHidden ? ' hide' : ''}`;

  const rowClass = (base: string, type: SubmittalLibraryType) =>
    `${base} ${type === activeType ? 'show-row' : ''}`;

  const query = searchValue.toLowerCase();

  return (
    <div className="col-xs-12 full-height col-md-6 col-lg-4 right-column-scrollable pt-0 pr-0">
      <div className="available_fields block-title">Defined Fields</div>
      <div className="col-xs-12 scrollable pr-0 overflow-x-hidden">
        <div className="col-xs-12">
          <div className="d-flex block-fields">
            {/* eslint-disable jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
            <div
              className={chipClass('user')}
              data-type="user"
              onClick={(e) =>
                activeType !== 'user' &&
                onChipClick(
                  ((e.target as HTMLElement).getAttribute('data-type') as
                    | SubmittalLibraryType
                    | null) ?? undefined
                )
              }
            >
              <i className="icon-baseline-account-box-24px" data-type="user" />{' '}
              <span data-type="user">User</span>{' '}
              {/* ★ The `+` carries NO `data-type` — see `activeType`'s note. */}
              <button id="open_user_defined_field_pop_up" className="btn-add btn add" onClick={onAddClick}>
                +
              </button>
            </div>
            <div
              className={chipClass('cms')}
              data-type="cms"
              onClick={(e) =>
                activeType !== 'cms' &&
                onChipClick(
                  ((e.target as HTMLElement).getAttribute('data-type') as
                    | SubmittalLibraryType
                    | null) ?? undefined
                )
              }
            >
              <i className="icon-cms-fields cms" data-type="cms" /> <span data-type="cms">CMS</span>
            </div>
            <div
              className={chipClass('application')}
              data-type="application"
              onClick={(e) =>
                activeType !== 'application' &&
                onChipClick(
                  ((e.target as HTMLElement).getAttribute('data-type') as
                    | SubmittalLibraryType
                    | null) ?? undefined
                )
              }
            >
              <i className="x-icon" data-type="application" />{' '}
              <span data-type="application">Application</span>
            </div>
            {/* eslint-enable jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
          </div>
        </div>
        <div className={searchClass('col-xs-12 submittal-search-field')}>
          <input
            type="search"
            className={searchClass('submittal-search-field search-field-input')}
            placeholder="Search..."
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>
        <div className="col-xs-12 fields-container" id="submittal-row-container">
          {/* ── USER ─────────────────────────────────────────────────────── */}
          <div className={rowClass('row h-100 submittal-row user-row hide-row', 'user')} data-type="user">
            <div className="col-xs-12 h-100 p-30">
              <div className="udf-scroll user-udf-scroll submittal-user-udf-scroll ml--5">
                <ul className="nav nav-tabs user-fields-block row" id="myTab" role="tablist">
                  {userFields.map((field) => {
                    const key = libraryItemKey('user', field.id);
                    const name = field.name ?? '';
                    const hidden = !name.toLowerCase().includes(query);
                    return (
                      /* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */
                      <li
                        key={field.id}
                        className={[
                          'nav-item',
                          'user-tab',
                          'each-element-row',
                          'col-xs-12',
                          `defined-li-${field.id}`,
                          activeItemKeys.has(key) ? 'active' : '',
                          tooltipItemKeys.has(key) ? 'with_tooltip_element' : '',
                          hidden ? 'hide' : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        id={`submittal_udf_item_field_${field.field_order}`}
                        data-name={name}
                        data-id={field.id}
                        ref={(el) => {
                          if (el) itemRefs.current.set(key, el);
                          else itemRefs.current.delete(key);
                        }}
                        onClick={() =>
                          onItemClick('user', {
                            key,
                            liId: String(field.id),
                            anchorId: field.id,
                            paneId: `submittal_user_defined_field_${field.id}`,
                          })
                        }
                      >
                        <div className="row">
                          <div className="col-xs-12">
                            <div className="row user-tab-row">
                              <div className="col-xs-1">
                                {field.udf_type_value === 'pdf' ? (
                                  <i className="icon-pdf udf-type-icon" />
                                ) : (
                                  <i className="icon-text-format-black-24dp udf-type-icon" />
                                )}
                              </div>
                              <div className="col-xs-11 padding-left-0">
                                {/* eslint-disable-next-line jsx-a11y/mouse-events-have-key-events */}
                                <a
                                  draggable="true"
                                  className="user-defiened-fields active ui-state-default submittal-draggable-item submittal-item-for-drag ui-draggable ui-draggable-handle"
                                  data-id={field.id}
                                  id={`submittal_user_defined_field_${field.id}-tab`}
                                  data-toggle="tab"
                                  href={`#submittal_user_defined_field_${field.id}`}
                                  role="tab"
                                  aria-controls={`submittal_user_defined_field_${field.id}-tab`}
                                  aria-selected="true"
                                  data-text={name}
                                  ref={(el) => {
                                    if (el) anchorRefs.current.set(key, el);
                                    else anchorRefs.current.delete(key);
                                  }}
                                  onMouseOver={() => onUserAnchorMouseOver(field)}
                                  onClick={(e) => e.preventDefault()}
                                  {...anchorDragProps}
                                >
                                  {/* ★ The template emits `{{name}} ` — with a
                                      trailing space. Kept. */}
                                  {labelOverrides[field.id] ?? name}{' '}
                                </a>
                              </div>
                            </div>
                            <div className="dropdown  dropdown_user_fields">
                              <div className="">
                                {/* eslint-disable-next-line jsx-a11y/mouse-events-have-key-events */}
                                <button
                                  data-parent=".user-udf-scroll"
                                  className={`ka-has-tooltip btn btn-udf-delete dropdown-toggle icon-more-actions remove delete-btn-content${
                                    topButtons.has(field.id) ? ' top' : ''
                                  }`}
                                  type="button"
                                  data-toggle="dropdown"
                                  data-id={field.id}
                                  tooltip="Delete"
                                  onMouseOver={(e) => flipTop(field.id, e.target as HTMLElement)}
                                  onClick={() => onRemoveClick(field.id)}
                                >
                                  <i data-id={field.id} className="icon-trash remove" />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </div>

          {/* ── CMS ──────────────────────────────────────────────────────── */}
          <div className={rowClass('row submittal-row cms-row hide-row', 'cms')} data-type="cms">
            <ul className="nav nav-tabs cms-fields-block" id="myTab" role="tablist">
              {cmsDocuments.map((doc) => {
                const key = libraryItemKey('cms', doc.value);
                return (
                  /* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */
                  <li
                    key={doc.value}
                    className={`each-element-row nav-item col-xs-12${
                      activeItemKeys.has(key) ? ' active' : ''
                    }`}
                    data-id={doc.value}
                    ref={(el) => {
                      if (el) itemRefs.current.set(key, el);
                      else itemRefs.current.delete(key);
                    }}
                    onClick={() =>
                      onItemClick('cms', {
                        key,
                        liId: doc.value,
                        cms: doc.value,
                        paneId: `submittal_cms_${doc.key}`,
                      })
                    }
                  >
                    <div className="col-xs-12 p-0">
                      <div className="col-xs-12">
                        <div className="row">
                          <div className="col-xs-12 cms-tab">
                            <div className="row d-flex">
                              <div className="col-xs-1">
                                <i className="icon-text-format-black-24dp udf-type-icon" />
                              </div>
                              <div className="col-xs-11 padding-left-0">
                                <a
                                  data-cms={doc.value}
                                  draggable="true"
                                  className="cms-fields cms-fields-a active ui-state-default submittal-draggable-item submittal-item-for-drag ui-draggable ui-draggable-handle"
                                  data-name={doc.value}
                                  data-type="cms_terms"
                                  id={`submittal_cms_${doc.key}-tab`}
                                  data-toggle="tab"
                                  href={`#submittal_cms_${doc.key}`}
                                  role="tab"
                                  aria-controls={`submittal_cms_${doc.key}`}
                                  aria-selected="true"
                                  data-text={doc.label}
                                  ref={(el) => {
                                    if (el) anchorRefs.current.set(key, el);
                                    else anchorRefs.current.delete(key);
                                  }}
                                  onClick={(e) => e.preventDefault()}
                                  {...anchorDragProps}
                                >
                                  {doc.label}
                                </a>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* ── APPLICATION ──────────────────────────────────────────────── */}
          <div
            className={rowClass('row h-100 submittal-row application-row hide-row', 'application')}
            data-type="application"
          >
            <div className="col-xs-12 h-100">
              <div className="udf-scroll user-udf-scroll submittal-app-user-udf-scroll">
                <ul className="nav nav-tabs app-fields-block row" id="myTab" role="tablist">
                  {appFields.map((field) => {
                    const key = libraryItemKey('application', field.value);
                    return (
                      /* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */
                      <li
                        key={field.id}
                        className={`nav-item user-tab each-element-row app-tab col-xs-12${
                          activeItemKeys.has(key) ? ' active' : ''
                        }`}
                        data-id={`app-${field.value}`}
                        ref={(el) => {
                          if (el) itemRefs.current.set(key, el);
                          else itemRefs.current.delete(key);
                        }}
                        onClick={() =>
                          onItemClick('application', {
                            key,
                            liId: `app-${field.value}`,
                            anchorId: field.id,
                            paneId: `submittal_${field.value}`,
                          })
                        }
                      >
                        <div className="">
                          <div className="col-xs-12">
                            <div className="row user-tab-row">
                              <div className="col-xs-1">
                                <i className="icon-pdf udf-type-icon" />
                              </div>
                              <div className="col-xs-11 padding-left-0">
                                {' '}
                                <a
                                  draggable="true"
                                  className={`app-fields active ui-state-default submittal-draggable-item submittal-item-for-drag submittal_${field.value}-tab ui-draggable ui-draggable-handle`}
                                  data-type="app_defined"
                                  data-id={field.id}
                                  data-toggle="tab"
                                  href={`#submittal_${field.value}`}
                                  role="tab"
                                  aria-controls={field.value}
                                  aria-selected="true"
                                  data-text={field.name}
                                  ref={(el) => {
                                    if (el) anchorRefs.current.set(key, el);
                                    else anchorRefs.current.delete(key);
                                  }}
                                  onClick={(e) => e.preventDefault()}
                                  {...anchorDragProps}
                                >
                                  {field.name}
                                </a>
                              </div>
                            </div>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div>
        {/* `render()` un-hides exactly one of these (`:189`). Q6: the numbers are
            the collection lengths and never react to the search. */}
        <div className={`fields-count ${activeType === 'user' ? '' : 'hide '}user submittal-fields-count`}>
          Total Fields : <span className="count">{counts.user}</span>
        </div>
        <div
          className={`fields-count ${
            activeType === 'application' ? '' : 'hide '
          }application submittal-fields-count`}
        >
          Total Fields : <span className="count">{counts.application}</span>
        </div>
        <div className={`fields-count ${activeType === 'cms' ? '' : 'hide '}cms submittal-fields-count`}>
          Total Fields : <span className="count">{counts.cms}</span>
        </div>
      </div>
    </div>
  );
};

export default FieldLibrary;
